import React, { useState, useEffect } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  TextField,
  Grid,
  Box,
  Typography,
  CircularProgress,
  Alert,
  Paper,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Divider,
  IconButton,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow
} from '@mui/material';
import LoginIcon from '@mui/icons-material/Login';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import AddIcon from '@mui/icons-material/Add';
import DeleteIcon from '@mui/icons-material/Delete';
import { DataService } from 'src/services/data-service';

interface Lease {
  id: string;
  refNumber: string;
  tenant: {
    fullName: string;
  };
  unit: {
    id: string;
    number: string;
  };
}

interface UnitAppliance {
  quantity: number;
  typeId: string;
  type: string | null;
  unit: any;
  id: string;
  active: boolean;
}

interface ApplianceType {
  id: string;
  name: string;
  description: string;
  active: boolean;
}

interface ApplianceCheckIn {
  applianceId: string;
  conditionStatus: string;
  notes: string;
}

interface CheckInDialogProps {
  open: boolean;
  onClose: () => void;
  lease: Lease;
  onSuccess: () => void;
}

const CheckInDialog: React.FC<CheckInDialogProps> = ({ open, onClose, lease, onSuccess }) => {
  const [loading, setLoading] = useState(false);
  const [appliancesLoading, setAppliancesLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [applianceTypes, setApplianceTypes] = useState<ApplianceType[]>([]);
  const [checkInDate, setCheckInDate] = useState<string>(new Date().toISOString().slice(0, 16));
  const [appliances, setAppliances] = useState<ApplianceCheckIn[]>([]);

  useEffect(() => {
    if (open && lease.unit.id) {
      fetchUnitAppliances();
      fetchApplianceTypes();
    }
  }, [open, lease.unit.id]);

  const fetchApplianceTypes = async () => {
    try {
      const response = await DataService.get('api/appliance/all');

      if (response.ok) {
        const responseData = await response.json();
        let typesList: ApplianceType[] = [];

        if (responseData.success && responseData.data) {
          typesList = responseData.data;
        } else if (Array.isArray(responseData)) {
          typesList = responseData;
        }

        // Filter only active appliances
        setApplianceTypes(typesList.filter(type => type.active));
      } else {
        throw new Error('Failed to fetch appliance types');
      }
    } catch (error) {
      console.error('Error fetching appliance types:', error);
    }
  };

  const fetchUnitAppliances = async () => {
    setAppliancesLoading(true);
    setError(null);
    try {
      const response = await DataService.get(`api/UnitAppliance/unit-list/${lease.unit.id}`);

      if (response.ok) {
        const responseData = await response.json();
        let appliancesList: UnitAppliance[] = [];

        if (responseData.success && responseData.data) {
          appliancesList = responseData.data;
        } else if (Array.isArray(responseData)) {
          appliancesList = responseData;
        }

        // Initialize appliances with default values
        const initialAppliances = appliancesList.map(appliance => ({
          applianceId: appliance.typeId,
          conditionStatus: 'Good',
          notes: ''
        }));
        setAppliances(initialAppliances);
      } else {
        throw new Error('Failed to fetch unit appliances');
      }
    } catch (error) {
      console.error('Error fetching unit appliances:', error);
      setError('Failed to load unit appliances. Please try again.');
    } finally {
      setAppliancesLoading(false);
    }
  };

  const handleApplianceChange = (index: number, field: keyof ApplianceCheckIn, value: string) => {
    const updatedAppliances = [...appliances];
    updatedAppliances[index] = {
      ...updatedAppliances[index],
      [field]: value
    };
    setAppliances(updatedAppliances);
  };

  const handleAddAppliance = () => {
    setAppliances([
      ...appliances,
      {
        applianceId: '',
        conditionStatus: 'Good',
        notes: ''
      }
    ]);
  };

  const handleRemoveAppliance = (index: number) => {
    const updatedAppliances = appliances.filter((_, i) => i !== index);
    setAppliances(updatedAppliances);
  };

  const getApplianceName = (applianceId: string): string => {
    const appliance = applianceTypes.find(type => type.id === applianceId);
    return appliance ? appliance.name : applianceId;
  };

  const handleSubmit = async () => {
    // Validate that all appliances have an appliance ID
    const invalidAppliances = appliances.filter(a => !a.applianceId);
    if (invalidAppliances.length > 0) {
      setError('Please select an appliance type for all appliances.');
      return;
    }

    setLoading(true);
    setError(null);

    try {
      const payload = {
        leaseId: lease.id,
        date: new Date(checkInDate).toISOString(),
        appliances: appliances
      };

      const response = await DataService.post('api/Lease/checkin', payload);

      if (response.ok) {
        onSuccess();
      } else {
        const errorData = await response.json();
        throw new Error(errorData.message || 'Failed to submit check-in');
      }
    } catch (error) {
      console.error('Error submitting check-in:', error);
      setError(error instanceof Error ? error.message : 'Failed to submit check-in. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const handleClose = () => {
    if (!loading) {
      setError(null);
      onClose();
    }
  };

  return (
    <Dialog
      open={open}
      onClose={handleClose}
      maxWidth="lg"
      fullWidth
    >
      <DialogTitle sx={{ bgcolor: 'primary.main', color: 'white', fontWeight: 'bold', py: 1.5 }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <LoginIcon />
          Check In - Lease #{lease.refNumber}
        </Box>
      </DialogTitle>
      <DialogContent sx={{ pt: 2, pb: 1, minHeight: '400px', maxHeight: '70vh', overflowY: 'auto' }}>
        {error && (
          <Alert severity="error" sx={{ mb: 1.5 }}>
            {error}
          </Alert>
        )}

        {/* Lease Information & Check-In Date in one row */}
        <Grid container spacing={2} sx={{ mb: 1.5 }}>
          <Grid item xs={12} md={6}>
            <Paper sx={{ p: 1.5, bgcolor: 'grey.50', height: '100%', display: 'flex', flexDirection: 'column', justifyContent: 'center' }}>
              <Typography variant="caption" color="text.secondary" fontWeight="bold" display="block" sx={{ mb: 0.5 }}>
                Lease Information
              </Typography>
              <Typography variant="body2" sx={{ mb: 0.25 }}>
                <strong>Tenant:</strong> {lease.tenant.fullName}
              </Typography>
              <Typography variant="body2">
                <strong>Unit:</strong> {lease.unit.number}
              </Typography>
            </Paper>
          </Grid>
          <Grid item xs={12} md={6}>
            <Box sx={{ height: '100%', display: 'flex', alignItems: 'center' }}>
              <TextField
                fullWidth
                label="Check-In Date & Time"
                type="datetime-local"
                value={checkInDate}
                onChange={(e) => setCheckInDate(e.target.value)}
                InputLabelProps={{
                  shrink: true,
                }}
                size="small"
                required
                sx={{
                  '& .MuiInputBase-root': {
                    height: 'auto',
                    minHeight: '40px'
                  }
                }}
              />
            </Box>
          </Grid>
        </Grid>

        <Divider sx={{ mb: 1.5 }} />

        {/* Appliances Section */}
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 1 }}>
          <Typography variant="subtitle1" sx={{ fontWeight: 'bold' }}>
            Unit Appliances Inspection
          </Typography>
          <Button
            variant="contained"
            startIcon={<AddIcon />}
            onClick={handleAddAppliance}
            size="small"
            disabled={appliancesLoading}
          >
            Add Appliance
          </Button>
        </Box>

        {appliancesLoading ? (
          <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: '150px' }}>
            <CircularProgress />
          </Box>
        ) : appliances.length === 0 ? (
          <Alert severity="info" sx={{ mb: 1 }}>
            No appliances added yet. Click "Add Appliance" to add one.
          </Alert>
        ) : (
          <TableContainer component={Paper} sx={{ maxHeight: 'calc(70vh - 280px)', overflowY: 'auto' }}>
            <Table size="small" sx={{ '& .MuiTableCell-root': { py: 0.75, px: 1 } }} stickyHeader>
              <TableHead>
                <TableRow sx={{ bgcolor: 'grey.100' }}>
                  <TableCell sx={{ fontWeight: 'bold', width: '5%', py: 0.5, bgcolor: 'grey.100' }}>#</TableCell>
                  <TableCell sx={{ fontWeight: 'bold', width: '30%', py: 0.5, bgcolor: 'grey.100' }}>Appliance Type</TableCell>
                  <TableCell sx={{ fontWeight: 'bold', width: '20%', py: 0.5, bgcolor: 'grey.100' }}>Condition</TableCell>
                  <TableCell sx={{ fontWeight: 'bold', width: '35%', py: 0.5, bgcolor: 'grey.100' }}>Notes</TableCell>
                  <TableCell sx={{ fontWeight: 'bold', width: '10%', textAlign: 'center', py: 0.5, bgcolor: 'grey.100' }}>Actions</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {appliances.map((appliance, index) => (
                  <TableRow key={index} hover>
                    <TableCell>{index + 1}</TableCell>
                    <TableCell>
                      <FormControl fullWidth size="small" required>
                        <Select
                          value={appliance.applianceId}
                          onChange={(e) => handleApplianceChange(index, 'applianceId', e.target.value)}
                          displayEmpty
                        >
                          <MenuItem value="" disabled>
                            Select Appliance
                          </MenuItem>
                          {applianceTypes.map((type) => (
                            <MenuItem key={type.id} value={type.id}>
                              {type.name}
                            </MenuItem>
                          ))}
                        </Select>
                      </FormControl>
                    </TableCell>
                    <TableCell>
                      <FormControl fullWidth size="small" required>
                        <Select
                          value={appliance.conditionStatus}
                          onChange={(e) => handleApplianceChange(index, 'conditionStatus', e.target.value)}
                        >
                          <MenuItem value="Excellent">Excellent</MenuItem>
                          <MenuItem value="Good">Good</MenuItem>
                          <MenuItem value="Fair">Fair</MenuItem>
                          <MenuItem value="Poor">Poor</MenuItem>
                          <MenuItem value="Damaged">Damaged</MenuItem>
                        </Select>
                      </FormControl>
                    </TableCell>
                    <TableCell>
                      <TextField
                        fullWidth
                        size="small"
                        value={appliance.notes}
                        onChange={(e) => handleApplianceChange(index, 'notes', e.target.value)}
                        placeholder="Add observations..."
                      />
                    </TableCell>
                    <TableCell align="center">
                      <IconButton
                        size="small"
                        color="error"
                        onClick={() => handleRemoveAppliance(index)}
                      >
                        <DeleteIcon fontSize="small" />
                      </IconButton>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>
        )}
      </DialogContent>
      <DialogActions sx={{ px: 2, py: 1.5 }}>
        <Button
          onClick={handleClose}
          disabled={loading}
          variant="outlined"
          size="small"
        >
          Cancel
        </Button>
        <Button
          onClick={handleSubmit}
          variant="contained"
          color="primary"
          size="small"
          disabled={loading || appliancesLoading || appliances.length === 0}
          startIcon={loading ? <CircularProgress size={20} /> : <CheckCircleIcon />}
        >
          {loading ? 'Submitting...' : 'Submit Check-In'}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default CheckInDialog;
